/**
 * Status handling for live products (Motor Live / PS Live).
 *
 * Note: lodash `_.lowerCase` splits camelCase into words ("policyBound" ->
 * "policy bound"), so it must not be used to compare against these keys.
 */

export function normalizeStatus(status) {
  if (status === "Not Complete") {
    return "notcomplete";
  }
  return String(status == null ? "" : status)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Canonical status definitions for dashboard, reports, and quote lists.
 * `key` is used for progress-bar grouping; `rawStatuses` are normalized keys.
 */
export const STATUS_DEFINITIONS = [
  {
    key: "notstarted",
    label: "Not Started",
    description:
      "Quote saved but premium not yet retrieved.",
    color: "gray",
    rawStatuses: ["", "notcomplete"],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "quotecreated",
    label: "Quote Created",
    description: "Premium obtained; quote not yet emailed to the customer.",
    color: "purple",
    rawStatuses: ["quotecreated"],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "consentemailsent",
    label: "Consent Email Sent",
    description:
      "Consent email sent; waiting for the customer to confirm before the indicative quote is emailed.",
    color: "cyan",
    rawStatuses: ["consentemailsent"],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "senttocustomer",
    label: "Sent To Customer",
    description: "Continue-quote email sent by the broker to the customer.",
    color: "info",
    rawStatuses: ["senttocustomer"],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "senttobroker",
    label: "Sent To Broker",
    description:
      "Continue-quote email sent to another broker instead of the customer.",
    color: "primary",
    rawStatuses: ["senttobroker"],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "referred",
    label: "Referred",
    description: "Motor: referred for manual underwriting review.",
    color: "yellow",
    rawStatuses: [
      "quotereferredbasedonunderwritingquestions",
      "quotereferredinportal",
    ],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "declined",
    label: "Declined",
    description: "Motor: declined based on underwriting answers.",
    color: "danger",
    rawStatuses: [
      "quotedeclinedbasedonunderwritingquestions",
      "quotedeclinedinportal",
    ],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "bound",
    label: "Bound",
    description:
      "Policy stage reached (Motor: bound via payment; P&E: customer completed disclosure).",
    color: "success",
    rawStatuses: ["policybound", "customercompleted", "complete"],
    showInProgress: true,
    showInReports: true,
  },
  {
    key: "documentssent",
    label: "Documents Sent",
    description: "P&E: quote or policy documents uploaded and sent.",
    color: "blue",
    rawStatuses: ["quotedocumentssent", "policydocumentssent"],
    showInProgress: true,
    showInReports: true,
  },
];

/** Legacy Ubind statuses — not shown in live-product dashboard/reports filters. */
const LEGACY_STATUS_LABELS = {
  cancelled: "Cancelled",
  complete: "Complete",
};

const RAW_STATUS_TO_DEFINITION = STATUS_DEFINITIONS.reduce((acc, def) => {
  def.rawStatuses.forEach((raw) => {
    acc[raw] = def;
  });
  return acc;
}, {});

/** Statuses that represent a bound / completed policy. */
export const BOUND_STATUSES = ["complete", "policybound", "customercompleted"];

/** Terminal statuses excluded from the open-quotes chart. */
export const NON_QUOTE_STATUSES = [
  ...BOUND_STATUSES,
  "cancelled",
  "quotedocumentssent",
  "policydocumentssent",
];

/** Excluded from dashboard progress bar totals (legacy / non-live). */
export const PROGRESS_EXCLUDED_STATUSES = ["cancelled"];

export function isBoundStatus(status) {
  return BOUND_STATUSES.includes(normalizeStatus(status));
}

export function isOpenQuoteStatus(status) {
  return !NON_QUOTE_STATUSES.includes(normalizeStatus(status));
}

/**
 * @param {string|null|undefined} status
 * @returns {string} Group key from STATUS_DEFINITIONS
 */
export function liveStatusGroupKey(status) {
  const normalized = normalizeStatus(status);
  const def = RAW_STATUS_TO_DEFINITION[normalized];
  if (def) {
    return def.key;
  }
  return normalized || "notstarted";
}

/**
 * @param {string|null|undefined} status
 * @returns {string} Human-readable label
 */
export function liveStatusLabel(status) {
  const normalized = normalizeStatus(status);
  const def = RAW_STATUS_TO_DEFINITION[normalized];
  if (def) {
    return def.label;
  }
  if (LEGACY_STATUS_LABELS[normalized]) {
    return LEGACY_STATUS_LABELS[normalized];
  }
  const raw = String(status == null ? "" : status);
  if (!raw) {
    return STATUS_DEFINITIONS[0].label;
  }
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * @param {string|null|undefined} status
 * @returns {string|null} Description for legend / help text
 */
export function liveStatusDescription(status) {
  const normalized = normalizeStatus(status);
  const def = RAW_STATUS_TO_DEFINITION[normalized];
  return def ? def.description : null;
}

/** Status options for live-product report filters. */
export const LIVE_STATUS_LABELS = STATUS_DEFINITIONS.filter(
  (def) => def.showInReports
).map((def) => def.label);

/** Progress breakdown for live product quotes, in display order. */
export const PROGRESS_STATUSES = STATUS_DEFINITIONS.filter(
  (def) => def.showInProgress
).map(({ key, label, description, color }) => ({
  key,
  name: label,
  description,
  color,
}));
